'use client'

import { useEffect } from 'react'

import { useDailyLoginPing } from '@/shared/hooks/useDailyLoginPing'
import { useOptimizedRecord } from '@/shared/hooks/useOptimizedQuery'
import { useDataStore } from '@/shared/lib/dataStore'
import { Data } from '@/shared/types/data'

interface DataInitializerProps {
  initialData: Data | null
}

export function DataInitializer({ initialData }: DataInitializerProps) {
  const { setData, data } = useDataStore()
  useDailyLoginPing()

  const { record: remoteVersion } = useOptimizedRecord<{ version: string }>({
    path: '/user/gamification/version',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 60000
  })

  const localVersion = data?.version
  const shouldFetchData =
    !!remoteVersion && remoteVersion.version !== localVersion

  const { record: remoteData, isFetched } = useOptimizedRecord<Data>({
    path: '/user/gamification/data',
    params: {
      version: remoteVersion?.version
    },
    enabled: shouldFetchData,
    skipInitialData: true
  })

  useEffect(() => {
    if (
      !remoteData ||
      !remoteVersion?.version ||
      remoteData.version !== remoteVersion.version
    ) {
      return
    }
    if (!isFetched) return
    setData(remoteData)
  }, [remoteData, remoteVersion?.version, isFetched, setData])

  useEffect(() => {
    if (initialData) {
      setData(initialData)
    }
  }, [initialData, setData])

  return null
}
